import { del, get, post, put } from "@/shared/utils/fetchApi";
import type { Memory, MemoryFact, MemoryInput, MemoryTarget } from "../types/MemoryTypes";
import { validityPayload } from "../utils/memoryView";

/**
 * Memory do Brain (AB#87149). UM caminho para os três níveis, com o escopo como
 * parâmetro e `product_id` no workspace — quem lê e altera é decidido pela api.
 */
export class MemoryService {
  private static workspaceQuery(target: MemoryTarget): string {
    return target.productId ? `?product_id=${encodeURIComponent(target.productId)}` : "";
  }

  static list(target: MemoryTarget): Promise<Memory[]> {
    const params = new URLSearchParams({ scope: target.scope, limit: "500" });
    if (target.productId) params.set("product_id", target.productId);
    return get<Memory[]>(`/memories?${params.toString()}`);
  }

  static create(target: MemoryTarget, input: MemoryInput): Promise<Memory> {
    return post<Memory>("/memories", {
      scope: target.scope,
      ...(target.productId ? { product_id: target.productId } : {}),
      kind: input.kind,
      content: input.content,
      ...validityPayload(input),
    });
  }

  /**
   * `workspaceId` é o workspace aberto: levar uma memória do My view para o workspace
   * precisa dizer para qual, e a tela do My view não tem `productId` no alvo.
   */
  static update(target: MemoryTarget, id: string, input: MemoryInput, workspaceId?: string | null): Promise<Memory> {
    const productId = target.productId ?? (input.scope === "workspace" ? workspaceId : null);
    return put<Memory>(`/memories/${id}`, {
      ...(productId ? { product_id: productId } : {}),
      ...(input.scope && input.scope !== target.scope ? { scope: input.scope } : {}),
      kind: input.kind,
      content: input.content,
      ...(input.validityChanged === false ? {} : validityPayload(input, { editing: true })),
    });
  }

  static remove(target: MemoryTarget, id: string): Promise<unknown> {
    return del(`/memories/${id}${MemoryService.workspaceQuery(target)}`);
  }

  static facts(target: MemoryTarget, id: string): Promise<MemoryFact[]> {
    return get<MemoryFact[]>(`/memories/${id}/facts${MemoryService.workspaceQuery(target)}`);
  }
}
